import { useEffect, useRef, useState } from "react";

/**
 * Animates a numeric value from 0 → `value` when the returned ref enters
 * the viewport. Uses an easeOutCubic curve over `duration` ms.
 * Returns [ref, currentValue].
 */
export function useCountUp(value, duration = 1200, threshold = 0.4) {
  const ref = useRef(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    let raf = null;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            const start = performance.now();
            const tick = (t) => {
              const elapsed = t - start;
              const p = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
              setCurrent(value * eased);
              if (p < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
            obs.disconnect();
          }
        });
      },
      { threshold }
    );
    obs.observe(el);

    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, duration, threshold]);

  return [ref, current];
}
